// Where a shared thing goes, and the invitation a stranger arrives with.
//
// Sharing uses the device's own share sheet where there is one — on a phone
// that is WhatsApp, Signal, the class chat — and falls back to the networks'
// own intent links. No SDK, no pixel: the link is the whole integration.

export function cardUrl(token: string): string {
  return `${window.location.origin}/k/${token}`;
}

export function inviteUrl(code: string): string {
  return `${window.location.origin}/e/${code}`;
}

export type Target = 'whatsapp' | 'linkedin' | 'x';

export function intentUrl(target: Target, text: string, url: string): string {
  const t = encodeURIComponent(text);
  const u = encodeURIComponent(url);
  switch (target) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case 'x':
      return `https://x.com/intent/post?text=${t}&url=${u}`;
  }
}

/** Whether this device has a share sheet. */
export const canShareNatively = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/** Opens the share sheet. False when the device has none or the parent closed it. */
export async function shareNatively(title: string, text: string, url: string): Promise<boolean> {
  if (!canShareNatively) return false;
  try {
    await navigator.share({ title, text, url });
    return true;
  } catch {
    return false;
  }
}

// The invitation a new family arrives with. A stranger follows /e/{code} or a
// card, lands on the sign-in page with ?ref=CODE, registers, confirms the
// address in another tab perhaps, and founds the family some minutes later —
// so the code waits in localStorage, not in memory. It is not a credential:
// it names a family that invited somebody, and the server checks the rest.
const REF_KEY = 'sokrates.ref';

/** Reads ?ref= off the address the app was opened at, keeps it, and tidies the URL. */
export function captureReferral(): void {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('ref');
    if (!code) return;
    localStorage.setItem(REF_KEY, code);
    url.searchParams.delete('ref');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    // Storage refused, as in a private window: the family just tests for
    // fourteen days instead of a month.
  }
}

export function pendingReferral(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

export function clearReferral(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    // nothing to clear
  }
}
